/**
 * StarterPicksRow — curated "who to draft first" strip for NEW users.
 *
 * Shows 3 famous-but-affordable stars + up to 5 cheap risers (server-
 * curated via GET /api/entities/starter-picks). Renders only when:
 *   • the PostHog `starter-picks` feature flag is on (default on), AND
 *   • the user's roster has fewer than 3 drafts (`visible` prop), AND
 *   • the user hasn't dismissed it (localStorage).
 */
import { useEffect, useRef, useState } from 'react';
import { formatFranc } from '../lib/utils';
import { entityAPI } from '../lib/api';
import { useFeatureFlag, useFeatureFlagVariant } from '../lib/useFeatureFlag';
import EntityAvatar from './EntityAvatar';
import { Badge } from './ui/badge';
import { Plus, X, Sparkles, TrendingUp } from 'lucide-react';
import { posthogCapture } from '../posthog.config';

const DISMISS_KEY = 'fws_starter_picks_dismissed';

// 2026-07 A/B (PostHog experiment `starter-picks-cta`) — headline copy test
// to see which framing lifts signup→roster_complete. Ships all variants; the
// split % is tuned from the PostHog dashboard with no redeploy.
const CTA_COPY = {
  control: 'Starter Picks — great first drafts',
  action: 'New here? Draft your first star →',
  social: "Fans' top first picks — start here",
};

export default function StarterPicksRow({ visible, onAdd, isOnTeam, budgetLeft, isDraftable }) {
  const enabled = useFeatureFlag('starter-picks', true);
  const ctaVariant = useFeatureFlagVariant('starter-picks-cta', 'control');
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [picks, setPicks] = useState(null);
  const shownRef = useRef(false);
  const show = enabled && visible && !dismissed;

  useEffect(() => {
    if (!show || picks) return;
    let cancelled = false;
    entityAPI.starterPicks()
      .then((res) => { if (!cancelled) setPicks(res.data); })
      .catch(() => { if (!cancelled) setPicks({ stars: [], risers: [] }); });
    return () => { cancelled = true; };
  }, [show, picks]);

  // 2026-08-15 (testing-agent MEDIUM) — hide picks from categories the
  // league disables or has already filled (custom slot_config leagues);
  // otherwise the + can only ever bounce off a server 400.
  const cards = (picks
    ? [...(picks.stars || []).map((e) => ({ e, group: 'star' })),
       ...(picks.risers || []).map((e) => ({ e, group: 'riser' }))]
    : []).filter(({ e }) => (isDraftable ? isDraftable(e) : true));

  useEffect(() => {
    if (show && cards.length > 0 && !shownRef.current) {
      shownRef.current = true;
      posthogCapture('starter_picks_shown', { count: cards.length });
    }
  }, [show, cards.length]);

  if (!show || cards.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  const add = (e, group) => {
    posthogCapture('starter_pick_add', { entity_id: e.id, group });
    onAdd?.(e);
  };

  return (
    <div className="mb-3 rounded-xl border border-border/60 bg-card p-3" data-testid="starter-picks-row">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold flex items-center gap-1.5" data-testid="starter-picks-title">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          {CTA_COPY[ctaVariant] || CTA_COPY.control}
        </p>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss starter picks"
          data-testid="starter-picks-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {cards.map(({ e, group }) => {
          const onTeam = isOnTeam?.(e.id);
          const cantAfford = (e.pricing?.price || 0) > budgetLeft;
          const disabled = onTeam || cantAfford;
          return (
            <div
              key={e.id}
              className={`flex items-center gap-2 min-w-[172px] px-2.5 py-2 rounded-lg border border-border/60 bg-background ${disabled ? 'opacity-50' : ''}`}
              data-testid={`starter-pick-card-${e.id}`}
            >
              <EntityAvatar entity={e} size={32} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{e.name}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <Badge variant="secondary" className="text-[9px] px-1 py-0 gap-0.5">
                    {group === 'star'
                      ? (<><Sparkles className="w-2.5 h-2.5" />Star</>)
                      : (<><TrendingUp className="w-2.5 h-2.5" />Riser</>)}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {formatFranc(e.pricing?.price)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => !disabled && add(e, group)}
                disabled={disabled}
                className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40"
                aria-label={onTeam ? `${e.name} already on team` : `Add ${e.name}`}
                data-testid={`starter-pick-add-${e.id}`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
